import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import LoadingButton from "../../../../components/LoadingButton";
import LucideIcon from "../../../../components/LucideIcon";
import { COMPANY_SIZES, INDUSTRIES } from "../../../../constants";
import {
  LogoValidationError,
  uploadCompanyLogo,
} from "../../../../utils/imageUpload";
import { CompanyDetails } from "../types";

// Which fields the AI enrichment step fills in for us (vs. ones the user typed). Used to
// show an "auto-filled" hint and an inferring spinner while we wait. (Mirrors the real
// company form's fields — `type` is not shown; it's derived from `industry` on save.)
export type CompanyDetailField =
  | "description"
  | "industry"
  | "size"
  | "location";

interface CompanyDetailsPanelProps {
  details: CompanyDetails;
  // Fields the user has manually edited — their values are never overwritten by inference.
  touched: Set<keyof CompanyDetails>;
  // True while personalization is still running (so empty inferred fields show a spinner).
  generating: boolean;
  // Which inferred fields have arrived from the backend (for the "auto-filled" hint).
  filledByAi: Set<CompanyDetailField>;
  onChange: (field: keyof CompanyDetails, value: string) => void;
  // Used to authorize the logo upload when the user replaces the auto-detected logo.
  getToken: () => Promise<string>;
}

const InferringAdornment: React.FC = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
    <LucideIcon name="Loader" size={14} />
    <Typography variant="caption">inferring…</Typography>
  </Box>
);

const AiFilledChip: React.FC = () => (
  <Chip
    size="small"
    icon={<LucideIcon name="Sparkles" size={12} />}
    label="auto-filled"
    sx={{ height: 20, "& .MuiChip-label": { px: 0.75, fontSize: 11 } }}
  />
);

export default function CompanyDetailsPanel({
  details,
  touched,
  generating,
  filledByAi,
  onChange,
  getToken,
}: CompanyDetailsPanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const logoUrl = (details.logoUrl || "").trim();
  // While Phase 1 is running and we haven't pulled a logo yet, the analyze step may still be
  // fetching one — show a spinner. Once a logo arrives (or the user uploads one) we show it;
  // otherwise we fall back to an upload placeholder.
  const detectingLogo = generating && !logoUrl;

  // Upload a replacement logo and point logoUrl at the returned, re-hosted URL.
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    setUploadError(null);
    try {
      const uploadedUrl = await uploadCompanyLogo(file, getToken);
      if (uploadedUrl) onChange("logoUrl", uploadedUrl);
    } catch (err) {
      if (err instanceof LogoValidationError) {
        setUploadError(err.message);
      } else {
        console.error("[CompanyDetailsPanel] logo upload failed", err);
        setUploadError("Failed to upload image. Please try again.");
      }
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Show the "inferring…" hint only for a field that is still empty, untouched, and
  // expected to be filled by the AI (i.e. while generation is in flight).
  const showInferring = (field: CompanyDetailField) =>
    generating &&
    !touched.has(field) &&
    !(details[field] || "").trim() &&
    !filledByAi.has(field);

  const showAiFilled = (field: CompanyDetailField) =>
    filledByAi.has(field) && !touched.has(field) && !!(details[field] || "").trim();

  const labelFor = (text: string, field: CompanyDetailField) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
        {text}
      </Typography>
      {showInferring(field) && <InferringAdornment />}
      {showAiFilled(field) && <AiFilledChip />}
    </Box>
  );

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Your company details
        </Typography>
        <Typography variant="caption" color="text.secondary">
          We're filling these in as we learn about you — review and fix anything before you continue.
        </Typography>
      </Box>

      {/* Logo slot: a spinner while the analyze step is still fetching one, then the detected
          logo (or an empty placeholder), with an upload control to replace it if it's wrong. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <Avatar
          src={logoUrl || undefined}
          variant="rounded"
          sx={{
            width: 56,
            height: 56,
            bgcolor: "action.hover",
            color: "text.secondary",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {detectingLogo ? (
            <CircularProgress size={20} />
          ) : (
            <LucideIcon name="Building2" size={24} />
          )}
        </Avatar>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
              Logo
            </Typography>
            {detectingLogo && <InferringAdornment />}
            {!detectingLogo && logoUrl && !touched.has("logoUrl") && <AiFilledChip />}
          </Box>
          <LoadingButton
            variant="outlined"
            size="small"
            loading={uploadingLogo}
            onClick={() => fileInputRef.current?.click()}
            startIcon={<LucideIcon name="Upload" size={14} />}
            sx={{ textTransform: "none", alignSelf: "flex-start" }}
          >
            {logoUrl ? "Replace logo" : "Upload logo"}
          </LoadingButton>
        </Box>
      </Box>
      {uploadError && (
        <Typography variant="caption" color="error">
          {uploadError}
        </Typography>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
        <TextField
          label="Company name"
          size="small"
          fullWidth
          value={details.name || ""}
          onChange={(e) => onChange("name", e.target.value)}
        />
        <TextField
          label="Website"
          size="small"
          fullWidth
          value={details.website || ""}
          onChange={(e) => onChange("website", e.target.value)}
        />

        <FormControl size="small" fullWidth>
          <InputLabel id="onboarding-industry-label">Industry</InputLabel>
          <Select
            labelId="onboarding-industry-label"
            label="Industry"
            value={INDUSTRIES.includes(details.industry || "") ? details.industry : ""}
            onChange={(e) => onChange("industry", e.target.value)}
            renderValue={(value) =>
              value ? (
                (value as string)
              ) : showInferring("industry") ? (
                <InferringAdornment />
              ) : (
                ""
              )
            }
          >
            {INDUSTRIES.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
          {showAiFilled("industry") && (
            <Box sx={{ mt: 0.5 }}>
              <AiFilledChip />
            </Box>
          )}
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel id="onboarding-size-label">Company size</InputLabel>
          <Select
            labelId="onboarding-size-label"
            label="Company size"
            value={COMPANY_SIZES.includes(details.size || "") ? details.size : ""}
            onChange={(e) => onChange("size", e.target.value)}
            renderValue={(value) =>
              value ? (
                `${value} employees`
              ) : showInferring("size") ? (
                <InferringAdornment />
              ) : (
                ""
              )
            }
          >
            {COMPANY_SIZES.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt} employees
              </MenuItem>
            ))}
          </Select>
          {showAiFilled("size") && (
            <Box sx={{ mt: 0.5 }}>
              <AiFilledChip />
            </Box>
          )}
        </FormControl>

        <Box sx={{ gridColumn: { xs: "1", sm: "1 / -1" } }}>
          {labelFor("Location", "location")}
          <TextField
            size="small"
            fullWidth
            placeholder="e.g. United States"
            value={details.location || ""}
            onChange={(e) => onChange("location", e.target.value)}
          />
        </Box>
      </Box>

      <Box>
        {labelFor("What your company does", "description")}
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={3}
          placeholder={showInferring("description") ? "Reading your site…" : "A short summary of your business"}
          value={details.description || ""}
          onChange={(e) => onChange("description", e.target.value)}
        />
      </Box>
    </Box>
  );
}
